import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useTheme } from 'styled-components';
import * as FeatherIcon from 'react-feather';
import { Logo, Typography } from '@/components/atoms';
import UrlMapper from '@/config/url-mapper';
import { ContactCardProps } from './ContactCard.types';
import {
    AgencyBadge,
    Avatar,
    AvatarRing,
    Blob,
    Body,
    Divider,
    DownloadButton,
    Header,
    IconBadge,
    InfoList,
    InfoText,
    LinkInfo,
    LogoWrapper,
    Wave,
    Wrapper,
} from './ContactCard.styles';

const ContactCard: React.FC<ContactCardProps> = ({ contact }) => {
    const { t } = useTranslation(); // Translations
    const theme = useTheme(); // Get current theme
    const router = useRouter(); // Router

    const { token } = router.query;

    return (
        <Wrapper>
            <Header>
                <Blob />

                <LogoWrapper>
                    <Logo variant="imagotype" color="negative" link={UrlMapper.homepage} alt="Chagod Tour" height={30} />
                </LogoWrapper>

                <Wave viewBox="0 0 1440 100" preserveAspectRatio="none">
                    <path d="M0,40 C360,100 1080,0 1440,60 L1440,100 L0,100 Z" fill={theme.colors.basic.white} />
                </Wave>
            </Header>

            <AvatarRing>
                <Avatar image={contact?.image?.url}>
                    {!contact?.image?.url && <FeatherIcon.User size={40} color={theme.colors.tertiary.tertiary50} strokeWidth={1.5} />}
                </Avatar>
            </AvatarRing>

            <Body>
                <Typography variant="h5" align="center" weight={700}>
                    {contact?.fullName}
                </Typography>

                {contact?.agency && (
                    <AgencyBadge>
                        <Typography variant="cap" weight={600}>
                            {contact.agency}
                        </Typography>
                    </AgencyBadge>
                )}

                <Divider />

                <InfoList>
                    <LinkInfo href={`mailto:${contact?.email}`}>
                        <IconBadge variant="blue">
                            <FeatherIcon.Mail size={18} color={theme.colors.basic.white} strokeWidth={2} />
                        </IconBadge>

                        <InfoText>
                            <Typography as="span" variant="btn" weight={600}>
                                {contact?.email}
                            </Typography>
                            <Typography as="span" variant="cap" color={theme.colors.neutral.neutral50}>
                                {t('contact:card.label.email')}
                            </Typography>
                        </InfoText>
                    </LinkInfo>

                    <LinkInfo href={`tel:${contact?.phoneStaff}`}>
                        <IconBadge variant="orange">
                            <FeatherIcon.Phone size={18} color={theme.colors.basic.white} strokeWidth={2} />
                        </IconBadge>

                        <InfoText>
                            <Typography as="span" variant="btn" weight={600}>
                                {contact?.phoneStaff}
                            </Typography>
                            <Typography as="span" variant="cap" color={theme.colors.neutral.neutral50}>
                                {t('contact:card.label.phone')}
                            </Typography>
                        </InfoText>
                    </LinkInfo>
                </InfoList>

                <DownloadButton href={`/api/contact-card/${token}`} download>
                    <FeatherIcon.Download size={18} strokeWidth={2} />
                    <Typography as="span" variant="btn" weight={600} color={theme.colors.basic.white}>
                        {t('contact:card.button.download')}
                    </Typography>
                </DownloadButton>
            </Body>
        </Wrapper>
    );
};

export default ContactCard;
